import { Link, createFileRoute } from "@tanstack/react-router";
import * as z from "zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback } from "react";
import { UsersTabContent } from "./-tabs/UsersTab";
import { GroupsTabContent } from "./-tabs/GroupsTab";
import { EntitiesTabContent } from "./-tabs/EntitiesTab";
import { BreadcrumbNav } from "@/components/ui/breadcrumb";
import {
  policyEntitiesQueryOptions,
  policyGroupsQueryOptions,
  policyQueryOptions,
  policyUsersQueryOptions,
} from "@/lib/queries/policies";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

const tabs = ["Entities", "Groups", "Users"] as const;
const searchSchema = z.object({
  tab: z.enum(tabs).catch("Entities").default("Entities"),
});
const paramsSchema = z.object({ id: z.coerce.number().int().nonnegative() });
export const Route = createFileRoute("/_app/policies_/$id")({
  validateSearch: zodValidator(searchSchema),
  params: {
    parse: paramsSchema.parse,
  },
  component: RouteComponent,
  loader: ({ context: { queryClient }, params: { id } }) => {
    [
      policyQueryOptions(id),
      policyUsersQueryOptions(id),
      policyGroupsQueryOptions(id),
      policyEntitiesQueryOptions(id),
    ].map((q: any) => queryClient.ensureQueryData(q));
  },
  pendingComponent: () => (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-64 w-full" />
    </div>
  ),
  staticData: {
    permissionGuards: [
      {
        resource: "policies",
      },
    ],
  },
});

function RouteComponent() {
  const { id } = Route.useParams();

  const { data: policy } = useSuspenseQuery(policyQueryOptions(id));

  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const setTab = useCallback(
    (selectedTab: typeof tab) =>
      navigate({
        to: ".",
        from: Route.fullPath,
        search: (s) => ({ ...s, tab: selectedTab }),
      }),
    [navigate, Route.fullPath],
  );

  return (
    <div className="space-y-6 p-6">
      <BreadcrumbNav
        items={[
          { label: "Policies", link: <Link to="/policies">Policies</Link> },
          { label: "Policy Details" },
        ]}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Name
                </label>
                <p className="mt-1 text-lg font-semibold">{policy.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Type
                </label>
                <div className="mt-1">
                  <Badge variant="outline">{policy.type}</Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Priority
                </label>
                <p className="mt-1 font-mono text-lg">{policy.priority}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Status
                </label>
                <div className="mt-1">
                  <StatusBadge variant={policy.is_active ? "success" : "error"}>
                    {policy.is_active ? "Active" : "Inactive"}
                  </StatusBadge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Preset
                </label>
                <div className="mt-1">
                  <StatusBadge
                    variant={policy.is_pre_ready ? "success" : "error"}
                  >
                    {policy.is_pre_ready ? "Yes" : "No"}
                  </StatusBadge>
                </div>
              </div>
            </div>

            {policy.created_by && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Created By
                </label>
                <p className="mt-1">
                  {policy.created_by.full_name} ({policy.created_by.email})
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Created At
                </label>
                <p className="mt-1">
                  {new Date(policy.created_at).toLocaleString()}
                </p>
              </div>
              {policy.updated_at && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Updated At
                  </label>
                  <p className="mt-1">
                    {new Date(policy.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs
        defaultValue="entities"
        className="w-full"
        value={tab}
        onValueChange={(t) => setTab(t as typeof tab)}
      >
        <TabsList className="grid w-full grid-cols-3">
          {tabs.map((t) => (
            <TabsTrigger key={t} value={t}>
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="Entities" className="mt-4">
          <EntitiesTabContent policyId={id} isPreReady={policy.is_pre_ready} />
        </TabsContent>

        <TabsContent value="Groups" className="mt-4">
          <GroupsTabContent policyId={id} />
        </TabsContent>

        <TabsContent value="Users" className="mt-4">
          <UsersTabContent policyId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
